import React, { useState, useCallback, forwardRef, useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'

import { convertMegabytesToBytes } from 'utils'

import { useSnackbar } from 'contexts'
import { CameraIcon, XIcon } from 'lucide-react'
import { useDropzone, type FileWithPath } from 'react-dropzone'

import { Stack, Box, Typography, Avatar, IconButton, FormControl, useTheme, alpha } from '@mui/material'
import { Button } from '@repo/ui'

interface ProfilePhotoFieldProps {
  label?: string
  value?: File | string | null
  onChange?: (file: File | null) => void
  name?: string
  defaultInitials?: string
  error?: string
  /**
   * 'avatar' (default) center-crops the upload to a square and shows a circular preview.
   * 'logo' keeps the upload's native aspect ratio (logos are used in emails etc., per
   * VERA-880) and shows it un-cropped in a rounded-square preview.
   */
  variant?: 'avatar' | 'logo'
  /** Button copy overrides (default 'Upload photo' / 'Change photo'). */
  uploadLabel?: string
  changeLabel?: string
}

export const ProfilePhotoField = forwardRef<HTMLInputElement, ProfilePhotoFieldProps>(
  ({ label, value, onChange, name, defaultInitials = 'U', error, variant = 'avatar', uploadLabel = 'Upload photo', changeLabel = 'Change photo' }, ref) => {
    const [preview, setPreview] = useState<string | null>(null)
    const theme = useTheme()

    const { open: notification } = useSnackbar()

    const onDrop = useCallback(
      (acceptedFiles: FileWithPath[], rejectedFiles: any[]) => {
        if (acceptedFiles.length > 0) {
          const raw = acceptedFiles[0]
          // No auto-crop: logos keep their native aspect ratio (used in emails etc.); avatars are
          // display-cropped to a circle via CSS. A square-validated "Owner Avatar" upload is a
          // separate, deferred VERA-880 item.
          const previewUrl = URL.createObjectURL(raw)
          setPreview(previewUrl)
          onChange?.(raw)
        }

        if (rejectedFiles.length > 0) {
          const { errors } = rejectedFiles[0]

          errors.forEach(error => {
            if (error.code === 'file-too-large') {
              notification({
                type: 'error',
                title: 'File is too large. Max image size 10MB.',
              })
            }

            if (error.code === 'file-invalid-type') {
              notification({
                type: 'error',
                title: 'You can upload "png", "jpg", "jpeg" file formats only',
              })
            }
          })
        }
      },
      [onChange, notification]
    )

    const { getRootProps, getInputProps, open: openDropzone } = useDropzone({
      accept: {
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      },
      noClick: true,
      noKeyboard: false,
      maxSize: convertMegabytesToBytes(10),
      maxFiles: 1,
      onDrop,
    })

    const manualInputRef = useRef<HTMLInputElement | null>(null)

    const handleOpenFileDialog = (event: React.MouseEvent) => {
      event.preventDefault()
      if (manualInputRef.current) {
        manualInputRef.current.click()
        return
      }
      openDropzone()
    }

    useEffect(() => {
      return () => {
        if (preview) {
          URL.revokeObjectURL(preview)
        }
      }
    }, [preview])

    const inputProps = getInputProps()
    const { ref: dropzoneInputRef, ...restInputProps } = inputProps

    const setInputRef = (node: HTMLInputElement | null) => {
      if (typeof dropzoneInputRef === 'function') {
        dropzoneInputRef(node)
      }

      manualInputRef.current = node

      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ;(ref as MutableRefObject<HTMLInputElement | null>).current = node
      }
    }

    const handleRemovePhoto = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (preview) {
        URL.revokeObjectURL(preview)
      }
      setPreview(null)
      onChange?.(null)
    }

    // Determine what to display in the avatar
    const getAvatarSrc = () => {
      if (preview) return preview
      if (typeof value === 'string') return value
      return undefined
    }

    const avatarSrc = getAvatarSrc()
    const showInitials = !avatarSrc

    return (
      <FormControl error={!!error}>
        {label && (
          <Typography mb={2} variant="body3">
            {label}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" spacing={3}>
          {/* Avatar Preview */}
          <Box position="relative">
            <Avatar
              src={avatarSrc}
              variant={variant === 'logo' ? 'rounded' : 'circular'}
              imgProps={variant === 'logo' ? { style: { objectFit: 'contain' } } : undefined}
              sx={{
                width: 96,
                height: 96,
                fontSize: '2rem',
                fontWeight: 600,
                // Match the canonical company-logo radius used everywhere else
                // (CompanyAvatar = 6px); the rounded variant's MUI default
                // otherwise reads rounder than the real avatars. Logo variant
                // only — leave the circular (owner) avatar untouched.
                ...(variant === 'logo' ? { borderRadius: '6px' } : {}),
                border: theme.palette.primary[100],
                bgcolor: theme.palette.primary[100],
                color: theme.palette.text.primary,
              }}
            >
              {showInitials ? defaultInitials.toUpperCase().slice(0, 2) : null}
            </Avatar>

            {/* Remove button - only show when there's a photo */}
            {avatarSrc && (
              <IconButton
                onClick={handleRemovePhoto}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 28,
                  height: 28,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                  },
                }}
              >
                <XIcon size={16} strokeWidth={2} />
              </IconButton>
            )}
          </Box>

          {/* Upload Button */}
          <Box>
            <Button
              {...getRootProps({
                onClick: handleOpenFileDialog,
              })}
              variant="secondary"
              // component="button"
              // type="button"
              // sx={{
              //   cursor: 'pointer',
              //   px: 3,
              //   py: 1.5,
              //   border: `1px solid ${theme.palette.grey[300]}`,
              //   borderRadius: 1,
              //   display: 'inline-flex',
              //   alignItems: 'center',
              //   gap: 1,
              //   backgroundColor: 'transparent',
              //   '&:hover': {
              //     borderColor: theme.palette.brand[600],
              //     bgcolor: alpha(theme.palette.brand[600], 0.05),
              //   },
              // }}
              startIcon={<CameraIcon size={20} strokeWidth={1.5} />}
            >
              <input {...restInputProps} ref={setInputRef} name={name} />
              <Typography variant="body2">
                {avatarSrc ? changeLabel : uploadLabel}
              </Typography>
            </Button>

            <Typography variant="body4" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              PNG, JPG up to 10MB
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Typography variant="body3" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </FormControl>
    )
  }
)
